import { useCallback } from 'react';
import { useDispatch, useSelector } from 'core/common/hooks';
import { enableUserSubscription } from 'core/user/store/actions';
import { PaymentError, PaymentFormType } from '../entities';
import {
  clearPaymentState,
  initializePaymentFormRequest,
  paymentFormFailed,
  paymentFormSucceed,
  paymentFormVerify,
  paymentMethodsRequest,
} from '../store/actions';
import {
  getPaymentError,
  getPaymentFormMerchantData,
  getPaymentFormPaypalData,
  getPaymentProcessStep,
  getRecurringData,
} from '../store/selectors';

type UsePaymentFormProps = {
  offerId: string;
  paymentFormType: PaymentFormType;
};

export function usePayment({ offerId, paymentFormType }: UsePaymentFormProps) {
  const dispatch = useDispatch();

  const paymentProcessStep = useSelector(getPaymentProcessStep);
  const paymentError = useSelector(getPaymentError);
  const paymentFormMerchantData = useSelector(getPaymentFormMerchantData);
  const paymentFormPaypalData = useSelector(getPaymentFormPaypalData);
  const paymentProcessingData = useSelector(getRecurringData);

  const doInitializePaymentFormRequest = useCallback(() => {
    dispatch(initializePaymentFormRequest(offerId, paymentFormType));
  }, [dispatch, offerId, paymentFormType]);

  const doPaymentFormVerify = useCallback(() => {
    dispatch(paymentFormVerify());
  }, [dispatch]);

  const doPaymentFormFailed = useCallback(
    (error: PaymentError) => {
      dispatch(paymentFormFailed(error));
    },
    [dispatch],
  );

  const doPaymentFormSucceed = useCallback(
    async (paymentId: string) => {
      const paymentMethodDetails = await dispatch(paymentMethodsRequest(paymentId));

      dispatch(paymentFormSucceed());
      dispatch(enableUserSubscription());

      return { paymentMethodDetails };
    },
    [dispatch],
  );

  const doClearPaymentState = useCallback(() => {
    dispatch(clearPaymentState());
  }, [dispatch]);

  return {
    paymentProcessStep,
    paymentError,
    paymentFormMerchantData,
    paymentFormPaypalData,
    paymentProcessingData,

    initializePaymentFormRequest: doInitializePaymentFormRequest,
    paymentFormVerify: doPaymentFormVerify,
    clearPaymentState: doClearPaymentState,
    paymentFormFailed: doPaymentFormFailed,
    paymentFormSucceed: doPaymentFormSucceed,
  };
}
